import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useArticles } from "@/data/hooks";
import { useLanguage } from "@/i18n/LanguageContext";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";

/**
 * CARNET — Magazine éditorial de NANDA SÁ.
 * 9 rubriques structurent le contenu autour des territoires, savoir-faire et
 * initiatives de la Guinée-Bissau.
 */
const RUBRIQUES = [
 { key: "all", fr: "Toutes", en: "All", pt: "Todas" },
 { key: "territoires", fr: "Territoires", en: "Territories", pt: "Territórios" },
 { key: "opportunites", fr: "Opportunités", en: "Opportunities", pt: "Oportunidades" },
 { key: "rencontres", fr: "Rencontres", en: "Encounters", pt: "Encontros" },
 { key: "savoir-faire", fr: "Savoir-faire", en: "Know-how", pt: "Saberes" },
 { key: "culture", fr: "Culture & Patrimoine", en: "Culture & Heritage", pt: "Cultura e Património" },
 { key: "voyage", fr: "Carnets de voyage", en: "Travel journals", pt: "Cadernos de viagem" },
 { key: "energie-innovation", fr: "Énergie & Innovation", en: "Energy & Innovation", pt: "Energia e Inovação" },
 { key: "entreprendre", fr: "Entreprendre", en: "Entrepreneurship", pt: "Empreender" },
 { key: "maison", fr: "Vie de NANDA SÁ", en: "NANDA SÁ Life", pt: "Vida NANDA SÁ" },
];

// Fallback mapping from existing category labels → rubrique keys
const CATEGORY_TO_RUBRIQUE = {
 // FR
 "Plantes & terroirs": "savoir-faire",
 "Énergie": "energie-innovation",
 "Architecture": "territoires",
 "Entreprise": "maison",
 "Portrait": "rencontres",
 "Territoire": "territoires",
 "Analyse": "opportunites",
 "Cuisine": "culture",
 // EN
 "Plants & terroirs": "savoir-faire",
 "Energy": "energie-innovation",
 "Enterprise": "maison",
 "Territory": "territoires",
 "Analysis": "opportunites",
 // PT
 "Plantas e terroirs": "savoir-faire",
 "Energia": "energie-innovation",
 "Arquitectura": "territoires",
 "Empresa": "maison",
 "Retrato": "rencontres",
 "Território": "territoires",
 "Análise": "opportunites",
};

const rubriqueOf = (article) =>
 article.rubrique || CATEGORY_TO_RUBRIQUE[article.category] || "maison";

export default function Carnet() {
 const ARTICLES = useArticles();
 const { locale } = useLanguage();
 const l = (fr, en, pt) => (locale === "en" ? en : locale === "pt" ? pt : fr);
 const dateLocale = locale === "en" ? "en-GB" : locale === "pt" ? "pt-PT" : "fr-FR";
 const formatDate = (iso) =>
 new Date(iso).toLocaleDateString(dateLocale, { day: "numeric", month: "long", year: "numeric" });

 const [rubrique, setRubrique] = useState("all");
 const filtered =
 rubrique === "all" ? ARTICLES : ARTICLES.filter((a) => rubriqueOf(a) === rubrique);

 const [hero,...rest] = filtered.length ? filtered : ARTICLES;

 return (
 <div data-testid="page-carnet">
 <section className="border-b border-nanda-line">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 pt-24 md:pt-40 pb-16 md:pb-24 grid grid-cols-1 md:grid-cols-12 gap-10">
 <FadeUp className="md:col-span-4">
 <div className="label-overline text-nanda-muted">{l("Le Magazine", "The Magazine", "A Revista")}</div>
 </FadeUp>
 <FadeUp delay={0.1} className="md:col-span-8">
 <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-balance leading-[1.0]">
 {l("Le Carnet NANDA SÁ.", "The NANDA SÁ Journal.", "O Caderno NANDA SÁ.")}
 </h1>
 <p className="mt-10 max-w-3xl text-nanda-inkSoft text-base md:text-lg leading-relaxed">
 {l(
 "Le magazine qui raconte la Guinée-Bissau : territoires, savoir-faire, rencontres, innovations, patrimoine et entreprises. Des contenus documentés, sensibles et utiles.",
 "The magazine that tells Guinea-Bissau: territories, know-how, encounters, innovations, heritage and enterprises. Documented, sensitive and useful content.",
 "A revista que conta a Guiné-Bissau: territórios, saberes, encontros, inovações, património e empresas. Conteúdos documentados, sensíveis e úteis."
 )}
 </p>
 </FadeUp>
 </div>
 </section>

 {/* Filtres rubriques */}
 <section className="border-b border-nanda-line sticky top-[64px] md:top-[80px] z-30 bg-nanda-bg" data-testid="carnet-rubriques">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-4 overflow-x-auto">
 <div className="flex flex-nowrap items-center gap-2">
 {RUBRIQUES.map((r) => (
 <button
 key={r.key}
 type="button"
 onClick={() => setRubrique(r.key)}
 data-testid={`rubrique-${r.key}`}
 className={`whitespace-nowrap label-overline px-4 py-2 border transition-colors duration-300 ${
 rubrique === r.key
 ? "border-nanda-ink bg-nanda-ink text-white"
 : "border-nanda-line text-nanda-inkSoft hover:border-nanda-ink hover:text-nanda-ink"
 }`}
 >
 {l(r.fr, r.en, r.pt)}
 </button>
 ))}
 </div>
 </div>
 </section>

 {/* Featured */}
 {hero && (
 <section className="border-b border-nanda-line">
 <Link
 to={`/carnet/${hero.slug}`}
 data-testid={`carnet-feature-${hero.slug}`}
 className="group block hover:bg-nanda-bgAlt transition-colors duration-500"
 >
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
 <div className="md:col-span-7 aspect-[16/10] overflow-hidden">
 <img
 src={hero.cover}
 alt={hero.title}
 loading="lazy"
 className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
 />
 </div>
 <div className="md:col-span-5">
 <div className="label-overline text-nanda-terracotta">
 {hero.category} · {formatDate(hero.date)}
 </div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-5 leading-[1.05] text-balance">
 {hero.title}
 </h2>
 <p className="mt-6 text-nanda-inkSoft text-base md:text-[17px] leading-relaxed max-w-md">
 {hero.excerpt}
 </p>
 <span className="mt-8 inline-flex items-center gap-3 label-overline text-nanda-ink group-hover:translate-x-1 transition-transform duration-500">
 {l("Lire", "Read", "Ler")} <ArrowUpRight size={14} strokeWidth={1.5} />
 </span>
 </div>
 </div>
 </Link>
 </section>
 )}

 {/* Grid */}
 <section className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28">
 {rest.length === 0 ? (
 <p className="text-nanda-muted text-center py-16">
 {l(
 "Cette rubrique s'enrichira prochainement. En attendant, explorez les autres.",
 "This section will grow soon. In the meantime, explore the others.",
 "Esta rubrica enriquecer-se-á em breve. Entretanto, explore as outras."
 )}
 </p>
 ) : (
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
 {rest.map((a) => (
 <StaggerItem key={a.slug}>
 <Link
 to={`/carnet/${a.slug}`}
 data-testid={`carnet-article-${a.slug}`}
 className="group block"
 >
 <div className="aspect-[4/3] overflow-hidden mb-5">
 <img
 src={a.cover}
 alt={a.title}
 loading="lazy"
 className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
 />
 </div>
 <div className="label-overline text-nanda-muted">
 {a.category} · {formatDate(a.date)}
 </div>
 <h3 className="font-serif text-2xl md:text-3xl mt-3 leading-tight text-nanda-ink group-hover:text-nanda-terracotta transition-colors duration-500">
 {a.title}
 </h3>
 <p className="mt-3 text-nanda-inkSoft text-[15px] leading-relaxed">{a.excerpt}</p>
 </Link>
 </StaggerItem>
 ))}
 </Stagger>
 )}
 </section>
 </div>
 );
}
